import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { Panel } from '@ydistri/ds';

interface ScrolledTableWrapperProps extends PropsWithChildren {
  offset?: number;
  minHeight?: number;
}

interface HeightBearingChildProps {
  height?: string;
}

interface VirtualTableChildProps extends HeightBearingChildProps {
  virtual: boolean;
  scroll?: Record<string, unknown>;
}

const isHeightBearingComponent = (
  child: React.ReactNode,
): child is React.ReactElement<HeightBearingChildProps> =>
  React.isValidElement(child) && typeof child.type !== 'string' && child.type !== React.Fragment;

const isVirtualTable = (
  child: React.ReactElement<HeightBearingChildProps>,
): child is React.ReactElement<VirtualTableChildProps> => {
  const { props } = child;
  const scroll = 'scroll' in props ? props.scroll : undefined;

  return (
    'virtual' in props &&
    typeof props.virtual === 'boolean' &&
    (scroll === undefined || (typeof scroll === 'object' && scroll !== null))
  );
};

/**
 * Wrapper component to provide a scrollable table with dynamic height based on the container height minus an optional offset.
 * @param offset - Offset to subtract from the container height
 * @param children
 * @param minHeight - Minimum height for the table
 */
const ScrolledTableWrapper: React.FC<ScrolledTableWrapperProps> = ({
  offset = 0,
  children,
  minHeight = 500,
}): React.JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState<number>(minHeight);

  useEffect(() => {
    if (ref.current) {
      const setHeight = () => {
        const computedHeight = (ref.current?.clientHeight ?? 60) - offset;
        const newHeight = Math.max(computedHeight, minHeight);
        setTableHeight(newHeight);
      };

      setHeight();

      const listener = () => setHeight();
      //compute height on window resize
      window.addEventListener('resize', listener);

      //cleanup listener on unmount
      return () => {
        window.removeEventListener('resize', listener);
      };
    }
  }, [minHeight, offset, ref]);

  const childrenWithHeightProp = useMemo(() => {
    return React.Children.map(children, child => {
      if (!isHeightBearingComponent(child)) {
        return child;
      }

      if (isVirtualTable(child)) {
        // if the table is virtual, we need to set the height on the scroll property instead of the height property to make it work properly
        return React.cloneElement(child, {
          scroll: { ...child.props.scroll, y: tableHeight },
        });
      }

      return React.cloneElement(child, { height: `${tableHeight}px` });
    });
  }, [children, tableHeight]);

  return (
    <Panel ref={ref} $noAutoHeight={true} $grow={1}>
      {childrenWithHeightProp}
    </Panel>
  );
};

export default ScrolledTableWrapper;
